import { useCallback } from 'preact/compat';

import type { IMessage } from '@stomp/stompjs';

import { useWidget } from '../../context/WidgetContext';
import { EditorLockType } from '../../types/editor';
import type { UploadedFileType } from '../../types/files';
import { hasCsatRatingTrigger } from '../../types/guards';
import type { ErrorMessage, Message } from '../../types/messages';
import {
  WebsocketErrorType,
  WebsocketEvents,
  WebsocketMessageContentType,
  type WebSocketMessage,
} from '../../types/ws-events';
import { storageManager, convertAttachmentsToFiles } from '../../utils';
import { useSubscription } from 'react-stomp-hooks';

import { LAST_SEEN_MESSAGE_ID } from '../../constants/localStorage';

interface Props {
  userId: string;
}

/**
 * Компонент для WebSocket-подписок.
 * Рендерится только когда userId определён, чтобы не подписываться
 * на `/user/null/queue/message-events`.
 */
export const WidgetSubscriptions = ({ userId }: Props) => {
  const { setMessages, setEditorLock, setError, setHasNewMessage, localeMessages } = useWidget();

  // Получение входящего сообщения от системы
  const onGetMessage = useCallback(
    (message: IMessage) => {
      const widgetMessage = JSON.parse(message.body) as WebSocketMessage;

      const { attachments, createDate, message: messageText, messageHtml, messageJson, ...restMessage } = widgetMessage;

      if (
        restMessage.messageContentType === WebsocketMessageContentType.JsonCsatRequest &&
        hasCsatRatingTrigger(widgetMessage as Message)
      ) {
        setEditorLock?.(EditorLockType.Closed);
      }

      if (restMessage.id !== Number(storageManager.getItem(LAST_SEEN_MESSAGE_ID))) {
        setHasNewMessage?.(true);
      }
      // Добавление сообщения в список сообщений в чате
      setMessages?.((prevMessages) => [
        {
          ...restMessage,
          createDate,
          messageHtml,
          message: messageText,
          attachments: convertAttachmentsToFiles(attachments) as UploadedFileType[],
          messageJson: messageJson
            ? {
                title: messageJson.title,
                titleHtml: messageJson.titleHtml,
                buttons: messageJson.buttons.flat(),
              }
            : '',
        } as Message,
        ...prevMessages,
      ]);
    },
    [setEditorLock, setHasNewMessage, setMessages]
  );

  // Получение сообщения об ошибке от рейт лимитера
  const onGetErrorMessage = useCallback(
    (message: IMessage) => {
      const errorMessage = JSON.parse(message.body) as ErrorMessage;
      const { errorType } = errorMessage;

      if (errorType === WebsocketErrorType.FileUploadRateLimiter) {
        setError?.(localeMessages.errors.rateLimitFiles);
      }

      if (errorType === WebsocketErrorType.MessageRateLimiter) {
        setError?.(localeMessages.errors.rateLimitMessages);
      }
    },
    [setError, localeMessages]
  );

  // Подписка на событие из вебсокета для получения реплики бота или агента
  useSubscription(`/user/${userId}/${WebsocketEvents.UserEvents}`, onGetMessage);

  // Подписка на сообщение от рейт лимитера
  useSubscription(`/user/${userId}/${WebsocketEvents.ErrorEvents}`, onGetErrorMessage);

  return null;
};
